import useConfig from "@/hooks/useConfig";
import { Switch } from "@/v2/components/ui/switch";

/**
 * A switch between two named options, with the active side in the tenant's
 * own colour.
 *
 * WHY THIS IS SHARED. The Watch page had grown two of these: Classic View /
 * Enhanced View, and Home Feed / Away Feed. They are the same control, but
 * only the first had moved off MUI, so the second was still a MUI `Switch`
 * inside a `Stack` with two `Typography` labels. Copying the first would have
 * made a second copy of the same twenty lines of inline styling, which is
 * what CLAUDE.md's "grep for one that already exists" rule is there to stop.
 *
 * STYLED INLINE, for the reason the v2 Switch itself gives: both callers
 * render on the v1 Watch page, inside Bootstrap's global cascade, where a
 * Tailwind utility for a padding or a gap quietly resolves to something else.
 * `.v2` scoping does not reach them, so utilities are not an option here even
 * though this file lives under v2.
 */
export function LabeledSwitch({
  offLabel,
  onLabel,
  checked,
  onCheckedChange,
  ariaLabel,
}: {
  /** Shown to the left, active when `checked` is false. */
  offLabel: string;
  /** Shown to the right, active when `checked` is true. */
  onLabel: string;
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
  /**
   * Defaults to `onLabel`. A switch is named for the side it lands on, not
   * for the pair: "Away Feed" tells a screen reader what pressing it does,
   * where "feed" would tell it nothing.
   */
  ariaLabel?: string;
}) {
  const configData = useConfig();
  const active = configData?.primary || "#0b4ea2";

  const label = (text: string, on: boolean) => (
    <span
      style={{
        color: on ? active : "#000",
        fontWeight: 600,
        fontSize: 15,
        lineHeight: 1.2,
        whiteSpace: "nowrap",
      }}
    >
      {text}
    </span>
  );

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "5px 10px",
      }}
    >
      {label(offLabel, !checked)}
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
        aria-label={ariaLabel ?? onLabel}
      />
      {label(onLabel, checked)}
    </div>
  );
}

export default LabeledSwitch;
